/**
 * An FF role as a tinted pill.
 *
 * Shared by the registers that list rows per role — Capacity Inputs and the Effort
 * Matrix — where the same role repeats down a long column and a plain "ADE"/"BDE"
 * gives the eye nothing to group by.
 *
 * ## The two tints are existing tokens
 *
 * ADE takes the accent pair (`--accent-soft` / `--accent-hover`) and BDE the
 * Sales_Cycle chip pair (`--sc-chip-bg` / `--sc-chip-txt`). Both are already used
 * elsewhere for labels that *identify* rather than alert, so no new colour enters the
 * palette and neither reads as a status. Geometry matches `CapacityStateBadge` and
 * `StatusBadge` — the three appear in the same tables, and any difference in pill
 * shape reads as a mistake.
 *
 * The role's name is inside the pill, so the colour is additive and never the only
 * carrier of meaning (R10.6, R18.8). R18.13's granularity rule is unaffected: this
 * names a role, never a person.
 */
import { ffRoleLabel } from '../ff-role-options';

export interface RoleBadgeProps {
  /** An FF role identifier; anything unrecognised renders uppercased with the BDE tint. */
  readonly role: string;
}

export function RoleBadge({ role }: RoleBadgeProps): React.JSX.Element {
  const isAde = role === 'ade';
  return (
    <span
      data-slot="role-badge"
      data-role={role}
      className="inline-flex w-fit shrink-0 items-center rounded-[var(--radius-badge)] px-1.5 py-px text-[10.5px] font-semibold leading-[17px] whitespace-nowrap"
      style={
        isAde
          ? { backgroundColor: 'var(--accent-soft)', color: 'var(--accent-hover)' }
          : { backgroundColor: 'var(--sc-chip-bg)', color: 'var(--sc-chip-txt)' }
      }
    >
      {ffRoleLabel(role)}
    </span>
  );
}
